<script setup lang="ts">
import { TABLE_HEADER_CELL_TYPE } from "./useTable";
import { SortDirection } from "./useTable";
import { useTableHeaderCell } from "./useTableHeaderCell";
import type { TableHeaderCellProps } from "./TableHeaderCellProps";

/**
 * **TableHeaderCell** (`e-table-header-cell`)
 *
 * Renders a header cell within a `<e-table-row>` inside `<e-table-head>`.
 * Supports sorting (click to cycle through none → asc → desc → none) and
 * column resizing via a draggable resize handle.
 *
 * Sorting and resize state is managed centrally by the `useTable` composable
 * via the shared stateStore — the component itself holds no local sort or resize state.
 *
 * @example
 * ```html
 * <!-- Basic header cell -->
 * <e-table-header-cell>Name</e-table-header-cell>
 *
 * <!-- Sortable header cell -->
 * <e-table-header-cell sortable>Name</e-table-header-cell>
 *
 * <!-- Header cell with fixed width -->
 * <e-table-header-cell width="200">Name</e-table-header-cell>
 *
 * <!-- Resizable columns (set on parent table) -->
 * <e-table column-sizing="resizable">
 *   <e-table-head>
 *     <e-table-row>
 *       <e-table-header-cell>Name</e-table-header-cell>
 *     </e-table-row>
 *   </e-table-head>
 * </e-table>
 * ```
 */
defineOptions({
  name: TABLE_HEADER_CELL_TYPE,
});

const props = withDefaults(defineProps<TableHeaderCellProps>(), {
  sortDirection: SortDirection.None,
  textOverflow: "truncate",
  colspan: 1,
  rowspan: 1,
  align: "left",
});

const { cellRef, variants, isSorted, sortIconName, isColumnResizable, isLastColumn, sortColumn, startResize } =
  useTableHeaderCell({ props });

defineExpose({ cellRef });
</script>

<style lang="css">
:host {
  display: contents;
}
</style>

<template>
  <div ref="cellRef" part="header-cell" :class="variants.base" @click="sortColumn()">
    <slot />
    <div v-show="isSorted" :class="variants.sortIcon">
      <e-icon :name="sortIconName" size="xs" aria-hidden="true" />
    </div>
    <div
      v-if="isColumnResizable && !isLastColumn"
      part="resize-handle"
      :class="variants.handle"
      @pointerdown.prevent.stop="startResize($event)"
    >
      <div :class="variants.handleBar" part="resize-handle-bar"></div>
    </div>
  </div>
</template>
